/* eslint-disable indent */
import { apiPrefix, appName, baseURL, scope } from '@/config/base';
import {
  httpCreateMedicalInsight,
  httpGetLatestTreatmentRecommendation,
  httpGetMedicalInsight,
  httpGetMedicalInsightHistory,
  httpGetMedicalInsightQuestion,
  httpGetNCCNGuideline,
  httpGetProjectGuideline,
} from '@/services/unionward';
import { getToken } from '@/utils/cookies';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { message } from '@sinohealth/butterfly-ui-components';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

/**
 * @description: 获取NCCN指南推荐
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:00:00
 */
export const useNCCNGuideline = () => {
  // 分析病例数据的加载状态
  const [analyzing, setAnalyzing] = useState(false);
  const [guidelineData, setGuidelineData] = useState<string[]>([]);
  const [guidelineParams, setGuidelineParams] = useState<string>();
  // NCCN指南确认弹窗
  const [nccnConfirmVisible, setNccnConfirmVisible] = useState(false);
  // 所选NCCN指南类型
  const [selectedNccnType, setSelectedNccnType] = useState<number | undefined>(undefined);
  // 是否已完成初始化
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * 获取NCCN指南推荐数据
   * @param patientId 患者ID
   */
  const httpGetNCCNGuidelineReq = async () => {
    try {
      setAnalyzing(true);
      const res = await httpGetNCCNGuideline(guidelineParams);
      if (res) {
        setSelectedNccnType(res);
        // 检查res是否已经存在于guidelineData中
        if (!guidelineData?.includes(res)) {
          // 如果不存在，将res添加到数组最前面
          setGuidelineData((prev) => (prev ? [res, ...prev] : [res]));
        }
      }
      setIsInitialized(true);
      setNccnConfirmVisible(true);
    } catch (err: any) {
      console.error('获取NCCN指南推荐失败', err);
      setNccnConfirmVisible(false);
    } finally {
      setGuidelineParams('');
      setAnalyzing(false);
    }
  };

  const httpGetProjectGuidelineReq = async () => {
    try {
      const res = await httpGetProjectGuideline();
      setGuidelineData(res);
    } catch (err: any) {
      console.error('获取项目指南失败', err);
    }
  };

  useEffect(() => {
    guidelineParams && httpGetNCCNGuidelineReq();
  }, [guidelineParams]);

  useEffect(() => {
    httpGetProjectGuidelineReq();
  }, []);

  return {
    guidelineData,
    nccnConfirmVisible,
    analyzing,
    selectedNccnType,
    isInitialized,
    setIsInitialized,
    setAnalyzing,
    setNccnConfirmVisible,
    setGuidelineParams,
    setSelectedNccnType,
  };
};

/**
 * @description: 获取治疗方案推荐（流式输出）
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:10:00
 */
export const useTreatmentRecommendation = () => {
  const token = getToken();
  const [treatmentParams, setTreatmentParams] = useState<any>();
  const [connectLoading, setConnectLoading] = useState(false); // 是否正在连接
  const [thinking, setThinking] = useState(false); // 是否正在思考（UI显示用）
  const thinkingRef = useRef(false); // 使用 ref 来实时跟踪思考状态
  const [thinkingContent, setThinkingContent] = useState<string>(''); // 思考过程内容
  const [answerContent, setAnswerContent] = useState<string>(''); // 回答内容
  const [abortController, setAbortController] = useState(new AbortController());
  const [isAnswering, setIsAnswering] = useState(false); // 是否正在回答
  const [generatedTime, setGeneratedTime] = useState<string>(''); // 治疗方案生成时间

  // 新增：会话管理
  const [sessionId, setSessionId] = useState<string>(''); // 会话ID
  const [cursor, setCursor] = useState<number>(0); // 游标位置
  const [isReconnecting, setIsReconnecting] = useState(false); // 是否正在重连
  const [isCompleted, setIsCompleted] = useState(false); // 是否已完成
  const [isOnline, setIsOnline] = useState(navigator.onLine); // 网络状态

  // 使用 ref 保存实时状态，避免闭包问题
  const sessionIdRef = useRef<string>('');
  const cursorRef = useRef<number>(0);
  const thinkingContentRef = useRef<string>('');
  const answerContentRef = useRef<string>('');
  const patientIdRef = useRef<string>('');
  const guidelineNameRef = useRef<string>('');
  const isAnsweringRef = useRef<boolean>(false); // 用于网络监听判断
  const isCompletedRef = useRef<boolean>(false); // 用于判断是否已完成
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null); // 重连定时器
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null); // 心跳检测定时器
  const lastSseMessageTimeRef = useRef<number>(Date.now()); // 上次收到SSE消息的时间戳
  const sessionStartTimeRef = useRef<number>(Date.now()); // 会话开始时间
  const hasThinkTagRef = useRef<boolean>(false); // 标记是否收到过<think>标签
  // 重连相关配置
  const reconnectAttemptsRef = useRef<number>(0); // 重连尝试次数
  const baseReconnectInterval = 3000; // 基础重连间隔（毫秒）
  const maxReconnectAttempts = 5; // 最大重连尝试次数
  const maxReconnectInterval = 30000; // 最大重连间隔（毫秒）
  const reconnectIntervalRef = useRef<number>(baseReconnectInterval); // 当前重连间隔

  // 保存会话状态到 localStorage
  const saveSession = () => {
    // 如果已完成，不保存会话（避免覆盖已清除的缓存）
    if (isCompletedRef.current) {
      console.log('⚠️ 会话已完成，跳过保存');
      return;
    }

    // 只有在有patientId时才保存会话，避免保存无效会话
    if (!patientIdRef.current) {
      console.warn('⚠️ 无法保存会话：缺少 patientId');
      return;
    }

    const session = {
      sessionId: sessionIdRef.current,
      patientId: patientIdRef.current,
      guidelineName: guidelineNameRef.current,
      thinkingContent: thinkingContentRef.current,
      answerContent: answerContentRef.current,
      cursor: cursorRef.current,
      isCompleted: isCompletedRef.current,
      generatedTime,
      savedAt: new Date().toISOString(), // 添加保存时间戳
    };

    // console.log('💾 保存会话状态:', session);

    try {
      localStorage.setItem(`treatment_session_${patientIdRef.current}`, JSON.stringify(session));
      console.log('✅ 会话状态保存成功');
    } catch (err) {
      console.error('❌ 保存会话状态失败', err);
    }
  };

  // 恢复会话状态
  const restoreSession = (patientId: string) => {
    try {
      const saved = localStorage.getItem(`treatment_session_${patientId}`);
      if (saved) {
        const session = JSON.parse(saved);
        // if (!session.isCompleted && session.sessionId) {
        //   return session;
        // }
        if (!session.isCompleted) {
          return session;
        }
      }
    } catch (err) {
      console.error('恢复会话状态失败', err);
    }
    return null;
  };

  // 清除会话状态
  const clearSession = (patientId?: string) => {
    const pid = patientId || patientIdRef.current;
    if (pid) {
      try {
        localStorage.removeItem(`treatment_session_${pid}`);
        console.log('✅ 已清除会话缓存:', `treatment_session_${pid}`);
      } catch (err) {
        console.error('清除会话状态失败', err);
      }
    }
    setSessionId('');
    sessionIdRef.current = '';
    setCursor(0);
    cursorRef.current = 0;
    // 注意：不重置 isCompleted，由调用方控制
  };

  // 更新 ref
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    thinkingContentRef.current = thinkingContent;
  }, [thinkingContent]);

  useEffect(() => {
    answerContentRef.current = answerContent;
  }, [answerContent]);

  useEffect(() => {
    isAnsweringRef.current = isAnswering;
  }, [isAnswering]);

  useEffect(() => {
    isCompletedRef.current = isCompleted;
  }, [isCompleted]);

  // 监听网络状态变化
  useEffect(() => {
    const handleOnline = () => {
      console.log('网络已恢复');
      setIsOnline(true);

      // 🔥 改进：使用 setTimeout 确保状态已更新
      setTimeout(() => {
        const hasSession = !!sessionIdRef.current;
        const notCompleted = !isCompleted;

        console.log('网络恢复，检查重连条件:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          isCompleted,
          hasSession,
          notCompleted,
        });

        if (hasSession && notCompleted) {
          console.log('✅ 满足重连条件，开始自动重连...');
          reconnect();
        } else {
          console.log('❌ 不满足重连条件，跳过重连');
        }
      }, 100); // 延迟 100ms 确保状态同步
    };

    const handleOffline = () => {
      console.log('网络已断开');
      setIsOnline(false);

      // 网络断开时立即保存当前状态
      if (sessionIdRef.current) {
        console.log('网络断开，保存当前治疗方案状态:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          patientId: patientIdRef.current,
        });
        saveSession();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isCompleted]); // 依赖 isCompleted，避免完成后还重连

  // 🔥 新增：心跳检测机制 - 定期检查网络状态并尝试重连
  useEffect(() => {
    // 🔥 关键改进：只要有 sessionId 且未完成，就启动心跳检测
    // 不再检查 isAnswering，因为网络断开后 isAnswering 会变成 false
    const shouldStartHeartbeat = sessionIdRef.current && !isCompleted;
    // const shouldStartHeartbeat = !isCompleted;

    // console.log('🫀 心跳检测条件检查:', {
    //   sessionId: sessionIdRef.current,
    //   isCompleted,
    //   isAnswering,
    //   isReconnecting,
    //   shouldStartHeartbeat,
    // });

    if (shouldStartHeartbeat) {
      console.log('✅ 启动心跳检测机制');

      heartbeatTimerRef.current = setInterval(() => {
        // 检查会话是否超时（30分钟）
        const isSessionTimeout = Date.now() - sessionStartTimeRef.current > 30 * 60 * 1000;

        if (isSessionTimeout) {
          console.log('⏰ 会话已超时，停止重连');
          // 清理会话状态
          clearSession();
          // 停止心跳检测
          if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
          }
          return;
        }

        // 检查SSE消息是否超时（10秒）
        const isSseTimeout = Date.now() - lastSseMessageTimeRef.current > 10000;

        const shouldReconnect =
          sessionIdRef.current &&
          !isCompleted &&
          !isAnswering &&
          !isReconnecting &&
          navigator.onLine &&
          isSseTimeout;

        // console.log('💓 心跳检测:', {
        //   sessionId: sessionIdRef.current,
        //   isCompleted,
        //   isAnswering,
        //   isReconnecting,
        //   navigator_onLine: navigator.onLine,
        //   sessionTimeout: isSessionTimeout,
        //   sseTimeout: isSseTimeout,
        //   shouldReconnect,
        // });

        if (shouldReconnect) {
          console.log('🔥 心跳检测触发重连！');
          reconnect();
        }
      }, 3000); // 每 3 秒检测一次

      return () => {
        if (heartbeatTimerRef.current) {
          console.log('🫀 停止心跳检测');
          clearInterval(heartbeatTimerRef.current);
          heartbeatTimerRef.current = null;
        }
      };
    }

    console.log('⚠️ 心跳检测未启动');
    return undefined;
  }, [sessionId, isCompleted, isAnswering, isReconnecting]);

  const contact = async (isReconnect = false) => {
    setConnectLoading(true);

    if (isReconnect) {
      setIsReconnecting(true);
    } else {
      // 首次连接，重置状态
      setThinking(false);
      thinkingRef.current = false;
      setThinkingContent('');
      setAnswerContent('');
      setIsAnswering(false);
      setSessionId('');
      sessionIdRef.current = '';
      setCursor(0);
      cursorRef.current = 0;
      setIsCompleted(false);
      thinkingContentRef.current = '';
      answerContentRef.current = '';
      lastSseMessageTimeRef.current = Date.now(); // 重置SSE消息时间
      sessionStartTimeRef.current = Date.now(); // 重置会话开始时间
      hasThinkTagRef.current = false; // 重置think标签标记
    }

    // 保存患者ID和指南名称
    if (treatmentParams?.patientId) {
      patientIdRef.current = treatmentParams.patientId;
    }
    if (treatmentParams?.guidelineName) {
      guidelineNameRef.current = treatmentParams.guidelineName;
    }

    // 构建请求头
    // 重连时通过请求头传递 Last-Event-ID（cursor）和 Request-ID（sessionId）
    const headers: Record<string, string> = {
      'X-Token': token || '',
      biz: scope,
      scope,
      app: appName,
      'Content-Type': 'application/json',
    };

    // 重连时添加断点续传的请求头
    if (isReconnect && sessionIdRef.current) {
      headers['Request-ID'] = sessionIdRef.current;
      headers['Last-Event-ID'] = String(cursorRef.current);
      console.log('重连请求头:', {
        'Request-ID': sessionIdRef.current,
        'Last-Event-ID': cursorRef.current,
      });
    }

    fetchEventSource(`${baseURL}${apiPrefix}/ai-advisor/treatment/recommendation`, {
      method: 'POST',
      headers,
      body: JSON.stringify(treatmentParams),
      signal: abortController.signal,
      openWhenHidden: true,
      // 🔥 关键：禁用自动重试，由我们手动控制重连
      async onopen(response) {
        if (response.ok) {
          console.log('✅ SSE 连接成功', isReconnect ? '(重连模式)' : '(首次连接)');
          setIsAnswering(true);
          if (!isReconnect) {
            setGeneratedTime(moment().format('YYYY-MM-DD HH:mm:ss'));
          }
          // 重连模式下保持 isReconnecting 为 true，直到收到实际数据
          // 首次连接时保持 connectLoading 为 true，直到收到实际数据（CHUNK_DELTA）
          // 只关闭 reconnecting 状态
          if (!isReconnect) {
            setIsReconnecting(false);
          }
        } else {
          console.error('❌ SSE 连接失败，状态码:', response.status);
          // 连接失败时抛出错误，阻止自动重试
          throw new Error(`连接失败: ${response.status}`);
        }
      },
      onmessage(msg: { data: any; id: string; event: string }) {
        // 更新最后收到SSE消息的时间
        lastSseMessageTimeRef.current = Date.now();

        const { event: type, id } = msg;
        const data = JSON.parse(msg.data || '{}');
        console.log('📨 收到 SSE 消息:', { type, id, data });

        // 1️⃣ SSE_ACK 事件：记录 requestId（相当于 sessionId）
        if (type === 'SSE_ACK') {
          if (data.requestId) {
            const requestId = data.requestId;
            // 只在首次连接时记录 sessionId，重连时不覆盖
            if (!sessionIdRef.current) {
              setSessionId(requestId);
              sessionIdRef.current = requestId;
              console.log('✅ 收到 SSE_ACK，记录 sessionId:', requestId);
              if (data.bizId) {
                console.log('📋 bizId:', data.bizId);
              }
              // 收到 sessionId 后立即保存
              saveSession();
            } else {
              console.log('⚠️ sessionId 已存在，跳过更新:', sessionIdRef.current);
            }
          }
          return;
        }

        // 2️⃣ SSE_HEARTBEAT 事件：心跳，保持连接状态
        if (type === 'SSE_HEARTBEAT') {
          console.log('💓 收到心跳，连接正常');
          // 心跳事件不更新游标，只维持连接状态
          // 确保状态正确
          if (!isAnswering) {
            setIsAnswering(true);
          }
          return;
        }

        // 3️⃣ CHUNK_DELTA 事件：处理增量文本块（数据正在输出）
        if (type === 'CHUNK_DELTA') {
          // 开始接收数据，关闭 loading 和 reconnecting 状态
          if (connectLoading) {
            setConnectLoading(false);
          }
          if (isReconnecting) {
            setIsReconnecting(false);
          }

          // 🔥 关键：只有 CHUNK_DELTA 事件才更新游标
          if (id !== undefined && id !== '') {
            const cursorId = Number(id);
            setCursor(cursorId);
            cursorRef.current = cursorId;
            console.log('📍 更新游标:', cursorId);
          }

          // 处理内容
          if (data.content) {
            const content = data.content;

            // 处理思考过程和回答内容
            if (content.startsWith('<think>')) {
              hasThinkTagRef.current = true;
              thinkingRef.current = true;
              setThinking(true);
              const thinkContent = content.replace('<think>', '');
              setThinkingContent((prev) => {
                const newContent = prev + thinkContent;
                thinkingContentRef.current = newContent;
                return newContent;
              });
            } else if (content === '</think>') {
              thinkingRef.current = false;
              setThinking(false);
            } else if (thinkingRef.current) {
              setThinkingContent((prev) => {
                const newContent = prev + content;
                thinkingContentRef.current = newContent;
                return newContent;
              });
            } else {
              setAnswerContent((prev) => {
                const newContent = prev + content;
                answerContentRef.current = newContent;
                return newContent;
              });
            }

            // 每收到数据就保存会话状态（用于断点续传）
            saveSession();
          }
          return;
        }

        // 4️⃣ SSE_REPLY_END 事件：结束
        if (type === 'SSE_REPLY_END') {
          const status = data.status;
          console.log('🏁 收到 SSE_REPLY_END，status:', status);

          if (status === 'completed') {
            // 正常完成（相当于之前的 finish=true）
            console.log('✅ 治疗方案推荐正常完成');
            console.log('📊 完成时的状态:', {
              patientId: patientIdRef.current,
              sessionId: sessionIdRef.current,
              cursor: cursorRef.current,
            });
            setAbortController(new AbortController());
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            // 先更新 ref，再设置状态，最后清除缓存
            isCompletedRef.current = true;
            setIsCompleted(true);
            clearSession();
          } else if (status === 'fail' || status === 'not_exist') {
            // 异常完成：失败或事件不存在/已过期
            const errorMsg = data.message || '治疗方案推荐异常结束';
            console.error('❌ 治疗方案推荐异常结束:', status, errorMsg);
            message.error(errorMsg);
            setAbortController(new AbortController());
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            isCompletedRef.current = true;
            setIsCompleted(true);
            clearSession();
          } else {
            // 未知状态
            console.warn('⚠️ 未知的结束状态:', status);
            message.warning('治疗方案推荐结束，状态未知');
            setAbortController(new AbortController());
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            isCompletedRef.current = true;
            setIsCompleted(true);
            clearSession();
          }
          return;
        }

        // 未知事件类型
        console.warn('⚠️ 未知的事件类型:', type);
      },
      onclose() {
        console.log('🔌 SSE 连接关闭');
        // 注意：onclose 可能在正常结束或异常断开时都会触发
        // 如果已经通过 SSE_REPLY_END 处理了完成状态，这里不需要重复处理
        if (!isCompleted) {
          console.log('⚠️ 连接关闭但未完成，可能需要重连');
          setAbortController(new AbortController());
          // 🔥 不要设置 isAnswering = false，保持状态以便重连
          setIsReconnecting(false);
        }
      },
      onerror(err) {
        console.error('❌ SSE 连接错误:', err);
        console.log('📊 错误时的状态:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          patientId: patientIdRef.current,
          isCompleted,
          isReconnecting,
        });

        // 🔥 关键改进：断开时不要设置 isAnswering = false
        // 因为会话未完成，系统会自动重连，应该保持 isAnswering = true 防止用户重复提交
        // setIsAnswering(false); // ❌ 不要设为 false
        setIsReconnecting(false);
        setConnectLoading(false);

        // 🔥 关键：不要设置 isCompleted = true，因为这只是网络错误，不是真正完成
        // 立即保存当前状态，以便重连
        if (sessionIdRef.current) {
          console.log('💾 保存会话状态以便重连');
          saveSession();
        }

        // 🔥 关键：抛出错误以阻止 fetchEventSource 的自动重试
        // 我们通过网络监听和心跳检测来手动控制重连
        throw err;
      },
    });
  };

  useEffect(() => {
    if (treatmentParams) {
      // 判断是否为重连模式
      const isReconnect = treatmentParams.isReconnect || false;
      contact(isReconnect);
    }
  }, [treatmentParams]);

  // 连接成功时重置重连计数器
  useEffect(() => {
    if (isAnswering) {
      reconnectAttemptsRef.current = 0;
      reconnectIntervalRef.current = baseReconnectInterval;
      console.log('🔄 重连计数器已重置');
    }
  }, [isAnswering]);

  // 停止连接
  const stopContact = () => {
    abortController.abort();
    setConnectLoading(false);
    setIsAnswering(false);
    setIsReconnecting(false);
    // 只在未完成时保存当前状态
    if (!isCompleted) {
      saveSession();
    }
  };

  // 重连方法
  const reconnect = () => {
    console.log('🔄 尝试重连治疗方案，当前状态:', {
      sessionId: sessionIdRef.current,
      cursor: cursorRef.current,
      patientId: patientIdRef.current,
      guidelineName: guidelineNameRef.current,
      isCompleted,
      isReconnecting,
      isAnswering,
      reconnectAttempts: reconnectAttemptsRef.current,
    });

    // 防止重复重连
    if (isReconnecting) {
      console.log('⚠️ 已在重连中，跳过');
      return;
    }

    // 检查重连次数是否已达上限
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('❌ 重连次数已达上限，停止自动重连');
      message.error('网络连接已断开，多次重连失败，请手动重新生成治疗方案');
      return;
    }

    if (sessionIdRef.current && !isCompleted) {
      console.log('✅ 满足重连条件，开始重连...');

      // 确保有必要的参数
      if (!patientIdRef.current || !guidelineNameRef.current) {
        console.error('❌ 缺少必要参数，尝试从 localStorage 恢复');
        const restored = restoreAndReconnect(patientIdRef.current);
        if (!restored) {
          console.error('❌ 无法恢复会话，重连失败');
        }
        return;
      }

      // 设置参数并触发重连
      setTreatmentParams({
        patientId: patientIdRef.current,
        guidelineName: guidelineNameRef.current,
        isReconnect: true,
      });

      // 增加重连尝试次数，计算下一次退避间隔
      const nextAttempts = reconnectAttemptsRef.current + 1;
      const nextInterval = Math.min(
        baseReconnectInterval * 2 ** (nextAttempts - 1),
        maxReconnectInterval,
      );

      reconnectAttemptsRef.current = nextAttempts;
      reconnectIntervalRef.current = nextInterval;
      console.log(`📊 更新重连状态：第${nextAttempts}次重连，下次间隔${nextInterval}ms`);
    } else {
      console.log('❌ 不满足重连条件:', {
        hasSessionId: !!sessionIdRef.current,
        isCompleted,
      });
    }
  };

  // 从已保存的会话恢复
  const restoreAndReconnect = (patientId: string) => {
    const session = restoreSession(patientId);
    if (session) {
      // 恢复状态
      setSessionId(session.sessionId || '');
      sessionIdRef.current = session.sessionId || '';
      setCursor(session.cursor);
      cursorRef.current = session.cursor;
      setThinkingContent(session.thinkingContent);
      thinkingContentRef.current = session.thinkingContent;
      setAnswerContent(session.answerContent);
      answerContentRef.current = session.answerContent;
      setGeneratedTime(session.generatedTime);
      patientIdRef.current = session.patientId;
      guidelineNameRef.current = session.guidelineName;

      // 标记为重连模式
      setIsReconnecting(true);

      // 设置参数并触发重连（通过 useEffect 自动触发 contact）
      setTreatmentParams({
        patientId: session.patientId,
        guidelineName: session.guidelineName,
        isReconnect: true, // 添加标记
      });

      return true;
    }
    return false;
  };

  // 组件卸载时清理定时器和断开连接
  useEffect(() => {
    return () => {
      console.log('🧹 组件卸载，清理资源');

      // 清理所有定时器
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // 如果连接未完成，保存当前状态并断开连接
      if (!isCompleted && sessionIdRef.current) {
        console.log('💾 组件卸载时保存未完成的会话状态:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          patientId: patientIdRef.current,
        });
        saveSession();

        // 主动断开 SSE 连接
        abortController.abort();
        console.log('🔌 已断开 SSE 连接');
      }
    };
  }, [isCompleted]);

  // 页面刷新/关闭前保存状态并关闭连接
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isCompleted && sessionIdRef.current) {
        console.log('💾 页面刷新/关闭前保存会话状态');
        saveSession();
        // 主动断开 SSE 连接
        abortController.abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isCompleted, abortController]);

  return {
    connectLoading,
    thinking,
    generatedTime,
    thinkingContent,
    answerContent,
    isAnswering,
    sessionId,
    cursor,
    isReconnecting,
    isCompleted,
    isOnline,
    setTreatmentParams,
    setConnectLoading,
    stopContact,
    setThinkingContent,
    setAnswerContent,
    reconnect,
    restoreAndReconnect,
    clearSession,
    guidelineNameRef, // 导出 guidelineNameRef
  };
};

/**
 * @description: 获取当前登录用户对指定患者的最新治疗推荐
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-22 15:20:00
 */
export const useLatestTreatmentRecommendation = () => {
  const [latestLoading, setLatestLoading] = useState<boolean>(false);
  const [latestTreatmentData, setLatestTreatmentData] = useState<any>(null);
  const [patientId, setPatientId] = useState<string>('');
  const [reloadLatest, setReloadLatest] = useState<boolean>(false);

  /**
   * 获取最新治疗推荐数据
   * @param patientId 患者ID
   */
  const httpGetLatestTreatmentRecommendationReq = async () => {
    try {
      setLatestLoading(true);
      const res = await httpGetLatestTreatmentRecommendation(patientId);
      setLatestTreatmentData(res);
    } catch (err: any) {
      console.error('获取最新治疗推荐失败', err);
    } finally {
      setLatestLoading(false);
      setReloadLatest(false);
    }
  };

  useEffect(() => {
    patientId && httpGetLatestTreatmentRecommendationReq();
  }, [patientId]);

  useEffect(() => {
    reloadLatest && httpGetLatestTreatmentRecommendationReq();
  }, [reloadLatest]);

  return {
    latestLoading,
    latestTreatmentData,
    setPatientId,
    setLatestTreatmentData,
    setReloadLatest,
  };
};

/**
 * @description: 获取病例智能洞察结果（流式输出）
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:30:00
 */
export const useMedicalInsight = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [insightData, setInsightData] = useState<any>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  /**
   * 获取病例智能洞察结果
   * @param params 请求参数
   */
  const fetchMedicalInsight = async (params: any) => {
    if (!params) {
      setError('请求参数不能为空');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setStreamingContent('');
      setIsCompleted(false);

      const res = await httpGetMedicalInsight(params);
      setInsightData(res);
      setIsCompleted(true);
    } catch (err: any) {
      setError(err.message || '获取病例智能洞察结果失败');
      console.error('获取病例智能洞察结果失败', err);
    } finally {
      setLoading(false);
    }
  };

  // 处理流式内容更新（如果接口支持）
  const updateStreamingContent = (content: string) => {
    setStreamingContent((prev) => prev + content);
  };

  return {
    loading,
    insightData,
    streamingContent,
    error,
    isCompleted,
    fetchMedicalInsight,
    updateStreamingContent,
  };
};

/**
 * @description: 查询病例智能洞察历史记录
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:40:00
 */
export const useMedicalInsightHistory = (callback?: (data: any) => void) => {
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<any>();
  const [historyParams, setHistoryParams] = useState<any>();
  const [reloadHistory, setReloadHistory] = useState<boolean>(false);

  /**
   * 查询病例智能洞察历史记录
   * @param params 请求参数
   */
  const httpGetMedicalInsightHistoryReq = async () => {
    try {
      setLoadingHistory(true);
      const res = await httpGetMedicalInsightHistory(historyParams);
      setHistoryData(res);
      callback?.(res);
    } catch (err: any) {
      console.error('查询病例智能洞察历史记录失败', err);
    } finally {
      setLoadingHistory(false);
      setReloadHistory(false);
    }
  };

  useEffect(() => {
    historyParams && httpGetMedicalInsightHistoryReq();
  }, [historyParams]);

  useEffect(() => {
    reloadHistory && httpGetMedicalInsightHistoryReq();
  }, [reloadHistory]);

  return {
    loadingHistory,
    historyData,
    setHistoryParams,
    setHistoryData,
    setLoadingHistory,
    setReloadHistory,
  };
};

/**
 * @description: 创建病历智能会话
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:50:00
 */
export const useMedicalInsightSession = () => {
  const [sessionInsightId, setSessionInsightId] = useState<string>('');
  const [sessionParams, setSessionParams] = useState<any>();
  const [questionData, setQuestionData] = useState<any>();
  const [loadingQuestion, setLoadingQuestion] = useState<boolean>(false);
  // 是否可以提问
  const [canAsk, setCanAsk] = useState(false);

  /**
   * 创建病历智能会话
   * @param params 请求参数
   */
  const httpCreateMedicalInsightReq = async () => {
    try {
      const res = await httpCreateMedicalInsight(sessionParams);
      setSessionInsightId(res);
      setCanAsk(true);
      httpGetMedicalInsightQuestionReq(res);
    } catch (err: any) {
      console.error('创建病历智能会话失败', err);
    }
  };

  /**
   * 获取病例智能洞察问题
   * @param insightId 病历智能会话ID
   */
  const httpGetMedicalInsightQuestionReq = async (insightId: string) => {
    try {
      setLoadingQuestion(true);
      const res = await httpGetMedicalInsightQuestion({ insightId });
      setQuestionData(res);
      if (!canAsk) {
        message.success('已根据病例分析生成推荐问题');
      } else {
        message.success('推荐问题已刷新');
      }
    } catch (err: any) {
      console.error('获取病例智能洞察问题失败', err);
    } finally {
      setLoadingQuestion(false);
    }
  };

  useEffect(() => {
    sessionParams && httpCreateMedicalInsightReq();
  }, [sessionParams]);

  return {
    sessionInsightId,
    canAsk,
    questionData,
    loadingQuestion,
    setSessionInsightId,
    setLoadingQuestion,
    setQuestionData,
    setSessionParams,
    setCanAsk,
    httpGetMedicalInsightQuestionReq,
  };
};

/**
 * @description: 获取治疗方案智能问答
 * @author: KaifengLi
 * @version: v2.35.0
 * @Date: 2025-05-21 15:50:00
 */
export const useTreatmentInsightQA = (insightId?: string) => {
  const token = getToken();
  const [qaParams, setQaParams] = useState<any>();
  const [connectLoading, setConnectLoading] = useState(false); // 是否正在连接
  const [thinking, setThinking] = useState(false); // 是否正在思考（UI显示用）
  const thinkingRef = useRef(false); // 使用 ref 来实时跟踪思考状态
  const [thinkingContent, setThinkingContent] = useState<string>(''); // 思考过程内容
  const [answerContent, setAnswerContent] = useState<string>(''); // 回答内容
  const [abortController, setAbortController] = useState(new AbortController());
  const [generatedTime, setGeneratedTime] = useState<string>(''); // 治疗方案生成时间
  const [completedQa, setCompletedQa] = useState<boolean>(false); // 是否完成问答
  const [isAnswering, setIsAnswering] = useState<boolean>(false); // 是否正在回答

  // 标记是否收到过<think>标签，用于判断使用哪种策略
  const hasThinkTagRef = useRef(false);

  // 新增：会话管理
  const [sessionId, setSessionId] = useState<string>(''); // 会话ID
  const [cursor, setCursor] = useState<number>(0); // 游标位置
  const [isReconnecting, setIsReconnecting] = useState(false); // 是否正在重连
  const [isCompleted, setIsCompleted] = useState(false); // 是否已完成
  const [currentQuestion, setCurrentQuestion] = useState<string>(''); // 当前问题

  // 配置常量
  const maxReconnectInterval = 30000; // 最大重连间隔（30秒）
  const baseReconnectInterval = 3000; // 基础重连间隔（3秒）
  const maxReconnectAttempts = 5; // 最大重连次数
  const sseTimeoutThreshold = 10000; // SSE超时阈值（10秒）
  const sessionTimeoutThreshold = 3600000; // 会话超时阈值（1小时）

  // 使用 ref 保存实时状态，避免闭包问题
  const sessionIdRef = useRef<string>('');
  const cursorRef = useRef<number>(0);
  const thinkingContentRef = useRef<string>('');
  const answerContentRef = useRef<string>('');
  const insightIdRef = useRef<string>('');
  const questionRef = useRef<string>('');
  const hasRestoredRef = useRef<boolean>(false); // 标记是否已尝试恢复
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null); // 心跳检测定时器
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null); // 重连定时器
  const isAnsweringRef = useRef<boolean>(false); // 用于网络监听判断
  const isCompletedRef = useRef<boolean>(false); // 是否已完成引用
  // 直接使用 ref 管理重连相关状态，不需要 useState
  const reconnectAttemptsRef = useRef<number>(0); // 重连尝试次数引用
  const reconnectIntervalRef = useRef<number>(3000); // 当前重连间隔引用
  const lastSseMessageTimeRef = useRef<number>(Date.now()); // 上次收到SSE消息的时间戳引用
  const sessionStartTimeRef = useRef<number>(Date.now()); // 会话开始时间引用

  // 保存会话状态到 localStorage
  const saveSession = () => {
    // 只有在有 insightId 和 question 时才保存
    if (!insightIdRef.current || !questionRef.current) return;

    const session = {
      sessionId: sessionIdRef.current || '', // 允许为空，首次连接时可能还没收到
      insightId: insightIdRef.current,
      question: questionRef.current,
      thinkingContent: thinkingContentRef.current,
      answerContent: answerContentRef.current,
      cursor: cursorRef.current,
      isCompleted,
      generatedTime,
      timestamp: Date.now(),
    };

    const key = `qa_session_${insightIdRef.current}`;
    try {
      localStorage.setItem(key, JSON.stringify(session));
      // console.log('已保存QA会话状态', { sessionId: session.sessionId, cursor: session.cursor });
    } catch (err) {
      console.error('保存QA会话状态失败', err);
    }
  };

  // 恢复会话状态
  const restoreSession = (targetInsightId: string, targetQuestion: string) => {
    const key = `qa_session_${targetInsightId}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const session = JSON.parse(saved);
        // console.log('尝试恢复QA会话', session);
        // 只恢复未完成的会话，且问题匹配
        // 注意：即使 sessionId 为空也尝试恢复，因为后端可能不需要 sessionId
        if (!session.isCompleted && session.question === targetQuestion) {
          return session;
        }
      }
    } catch (err) {
      console.error('恢复QA会话状态失败', err);
    }
    return null;
  };

  // 清除会话状态
  const clearSession = (targetInsightId?: string) => {
    const id = targetInsightId || insightIdRef.current;

    if (id) {
      const key = `qa_session_${id}`;
      try {
        localStorage.removeItem(key);
      } catch (err) {
        console.error('清除QA会话状态失败', err);
      }
    }

    // 重置状态
    setSessionId('');
    sessionIdRef.current = '';
    setCursor(0);
    cursorRef.current = 0;
    setIsCompleted(false);
    setCompletedQa(true);
    setIsAnswering(false);
    setIsReconnecting(false);
    setQaParams(undefined);
  };

  // 更新 ref
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    thinkingContentRef.current = thinkingContent;
  }, [thinkingContent]);

  useEffect(() => {
    answerContentRef.current = answerContent;
  }, [answerContent]);

  useEffect(() => {
    isAnsweringRef.current = isAnswering;
  }, [isAnswering]);

  // 🔥 新增：监听网络状态变化
  useEffect(() => {
    const handleOnline = () => {
      // console.log('🌐 QA网络已恢复 (online 事件)');

      setTimeout(() => {
        const hasSession = !!sessionIdRef.current;
        const notCompleted = !isCompleted;
        const notReconnecting = !isReconnecting;
        const notAnswering = !isAnswering;
        const sseTimeout = Date.now() - lastSseMessageTimeRef.current > sseTimeoutThreshold;

        console.log('QA网络恢复，检查重连条件:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          insightId: insightIdRef.current,
          question: questionRef.current,
          isCompleted,
          isReconnecting,
          isAnswering,
          sseTimeout,
          hasSession,
          notCompleted,
          notReconnecting,
          notAnswering,
        });

        // 网络恢复时，如果SSE已超时且满足其他条件，立即触发重连
        if (
          hasSession &&
          notCompleted &&
          notReconnecting &&
          notAnswering &&
          sseTimeout &&
          insightIdRef.current &&
          questionRef.current
        ) {
          console.log('✅ QA网络恢复且SSE超时，立即重连');
          reconnect();
        } else {
          console.log('❌ QA网络恢复，不满足立即重连条件，等待心跳检测');
        }
      }, 100);
    };

    const handleOffline = () => {
      // console.log('🔌 QA网络已断开 (offline 事件)');

      if (sessionIdRef.current || (insightIdRef.current && questionRef.current)) {
        console.log('QA网络断开，保存当前状态:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          insightId: insightIdRef.current,
          question: questionRef.current,
        });
        saveSession();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isCompleted, isReconnecting, isAnswering]);

  // 🔥 新增：心跳检测机制 - 定期检查网络状态并尝试重连
  useEffect(() => {
    // 必须有 sessionId 才启动心跳检测（说明已经建立过连接）
    const shouldStartHeartbeat = sessionIdRef.current && !isCompleted;

    // console.log('🫀 QA心跳检测条件检查:', {
    //   sessionId: sessionIdRef.current,
    //   insightId: insightIdRef.current,
    //   question: questionRef.current,
    //   isCompleted,
    //   isAnswering,
    //   isReconnecting,
    //   shouldStartHeartbeat,
    // });

    if (shouldStartHeartbeat) {
      // console.log('✅ 启动QA心跳检测机制');

      const check = () => {
        // 检查会话是否超时
        const isSessionTimeout = Date.now() - sessionStartTimeRef.current > sessionTimeoutThreshold;

        if (isSessionTimeout) {
          console.log('⏰ QA会话已超时，停止重连');
          // 清理会话状态
          clearSession();
          return;
        }

        // 检查重连条件：结合网络状态和SSE超时判断
        const shouldReconnect =
          sessionIdRef.current &&
          !isCompleted &&
          !isAnswering &&
          !isReconnecting &&
          navigator.onLine &&
          Date.now() - lastSseMessageTimeRef.current > sseTimeoutThreshold &&
          reconnectAttemptsRef.current < maxReconnectAttempts;

        // console.log('💓 QA心跳检测:', {
        //   sessionId: sessionIdRef.current,
        //   cursor: cursorRef.current,
        //   isCompleted,
        //   isAnswering,
        //   isReconnecting,
        //   navigator_onLine: navigator.onLine,
        //   sseTimeout: Date.now() - lastSseMessageTimeRef.current > sseTimeoutThreshold,
        //   reconnectAttempts: reconnectAttemptsRef.current,
        //   shouldReconnect,
        // });

        if (shouldReconnect) {
          console.log(`🔥 QA心跳检测触发重连！（第${reconnectAttemptsRef.current + 1}次）`);
          reconnect();
        }

        // 使用setTimeout实现下一次检查，间隔为当前退避间隔
        heartbeatTimerRef.current = setTimeout(check, reconnectIntervalRef.current);
      };

      // 启动第一次检查
      heartbeatTimerRef.current = setTimeout(check, reconnectIntervalRef.current);

      return () => {
        if (heartbeatTimerRef.current) {
          console.log('🫀 停止QA心跳检测');
          clearTimeout(heartbeatTimerRef.current);
          heartbeatTimerRef.current = null;
        }
      };
    }

    console.log('⚠️ QA心跳检测未启动');
    return undefined;
  }, [sessionId, isCompleted, isAnswering, isReconnecting]);

  const contact = async (isReconnect = false) => {
    setConnectLoading(true);
    setIsAnswering(true);

    if (isReconnect) {
      setIsReconnecting(true);
    } else {
      // 首次连接，重置状态
      setThinking(false);
      thinkingRef.current = false;
      setThinkingContent('');
      setAnswerContent('');
      setSessionId('');
      sessionIdRef.current = '';
      setCursor(0);
      cursorRef.current = 0;
      setIsCompleted(false);
      thinkingContentRef.current = '';
      answerContentRef.current = '';
      // 重置标记
      hasThinkTagRef.current = false;
      // 重置重连状态 - 直接更新 ref
      reconnectAttemptsRef.current = 0;
      reconnectIntervalRef.current = baseReconnectInterval;
      // 设置会话开始时间 - 直接更新 ref
      sessionStartTimeRef.current = Date.now();
    }

    // 保存当前问题信息
    if (qaParams?.insightId) {
      insightIdRef.current = qaParams.insightId;
    }
    if (qaParams?.question) {
      questionRef.current = qaParams.question;
      setCurrentQuestion(qaParams.question);
    }

    // 🔥 关键改动：首次连接时立即保存初始状态到 localStorage
    // 这样即使用户立即切换页面，也能恢复问题和连接状态
    if (!isReconnect && insightIdRef.current && questionRef.current) {
      saveSession();
    }

    // 构建请求头
    // 重连时通过请求头传递 Last-Event-ID（cursor）和 Request-ID（sessionId）
    const headers: Record<string, string> = {
      'X-Token': token || '',
      biz: scope,
      scope,
      app: appName,
      'Content-Type': 'application/json',
    };

    // 🔥 重连时添加断点续传的请求头
    if (isReconnect && sessionIdRef.current) {
      headers['Request-ID'] = sessionIdRef.current; // 传递 sessionId
      headers['Last-Event-ID'] = String(cursorRef.current); // 传递游标位置
      console.log('🔄 QA 重连请求头:', {
        'Request-ID': sessionIdRef.current,
        'Last-Event-ID': cursorRef.current,
        insightId: insightIdRef.current,
        question: questionRef.current,
      });
    }

    fetchEventSource(`${baseURL}${apiPrefix}/ai-advisor/medical/insight/qa`, {
      method: 'POST',
      headers,
      body: JSON.stringify(qaParams),
      // abortController.signal 提供了一个信号对象给 fetchEventSource 函数。
      // 如果在任何时候你想取消正在进行的 fetch 操作，你可以调用
      // abortController.abort()。这会发出关联任务的信号，你可以使用
      // AbortController 的信号来检查异步操作是否已被取消。
      signal: abortController.signal,
      openWhenHidden: true, // 切换标签页时连接不关闭
      async onopen(response) {
        if (response.ok) {
          // 连接成功
          console.log('✅ QA SSE 连接成功', isReconnect ? '(重连模式)' : '(首次连接)');
          if (!isReconnect) {
            setGeneratedTime(moment().format('YYYY-MM-DD HH:mm:ss'));
          }

          // 重连成功，重置重连状态 - 直接更新 ref
          if (isReconnect) {
            console.log('✅ QA重连成功，重置重连状态');
            reconnectAttemptsRef.current = 0;
            reconnectIntervalRef.current = baseReconnectInterval;
          }
        } else {
          console.error('❌ QA SSE 连接失败，状态码:', response.status);
          // 连接失败时抛出错误，阻止自动重试
          throw new Error(`QA连接失败: ${response.status}`);
        }
        setConnectLoading(false);
        setIsReconnecting(false);
      },
      onmessage(msg: { data: any; id: string; event: string }) {
        const { event: type, id } = msg;
        const data = JSON.parse(msg.data || '{}');

        // 更新上次收到SSE消息的时间戳 - 直接更新 ref
        lastSseMessageTimeRef.current = Date.now();

        console.log('📨 收到 QA SSE 消息:', { type, id, data });

        // 1️⃣ SSE_ACK 事件：记录 requestId（相当于 sessionId）
        if (type === 'SSE_ACK') {
          if (data.requestId) {
            const requestId = data.requestId;
            // 只在首次连接时记录 sessionId，重连时不覆盖
            if (!sessionIdRef.current) {
              setSessionId(requestId);
              sessionIdRef.current = requestId;
              // console.log('✅ QA 收到 SSE_ACK，记录 sessionId:', requestId);
              if (data.bizId) {
                console.log('📋 QA bizId:', data.bizId);
              }
              // 收到 sessionId 后立即保存
              saveSession();
            } else {
              console.log('⚠️ QA sessionId 已存在，跳过更新:', sessionIdRef.current);
            }
          }
          return;
        }

        // 2️⃣ SSE_HEARTBEAT 事件：心跳，保持连接状态
        if (type === 'SSE_HEARTBEAT') {
          // console.log('💓 QA 收到心跳，连接正常');
          // 心跳事件不更新游标，只维持连接状态
          // 确保状态正确，保持 loading 显示
          if (!isAnswering) {
            setIsAnswering(true);
          }
          if (!connectLoading && !answerContentRef.current) {
            // 如果还没有收到任何回答内容，保持 loading 状态
            setConnectLoading(true);
          }
          return;
        }

        // 3️⃣ CHUNK_DELTA 事件：处理增量文本块（数据正在输出）
        if (type === 'CHUNK_DELTA') {
          // 🔥 关键：只有 CHUNK_DELTA 事件才更新游标
          if (id !== undefined && id !== '') {
            const cursorId = Number(id);
            setCursor(cursorId);
            cursorRef.current = cursorId;
            // console.log('📍 QA 更新游标:', cursorId);
          }

          // 处理内容
          if (data.content) {
            const content = data.content;

            // 检查是否有<think>标签
            if (content.startsWith('<think>')) {
              // 标记为有<think>标签的处理策略
              hasThinkTagRef.current = true;

              // 思考过程开始
              thinkingRef.current = true;
              setThinking(true);
              // 提取<think>后面的内容添加到思考内容中
              const thinkContent = content.replace('<think>', '');
              setThinkingContent((prev) => {
                const newContent = prev + thinkContent;
                thinkingContentRef.current = newContent;
                return newContent;
              });
            } else if (content === '</think>') {
              // 思考过程结束
              thinkingRef.current = false;
              setThinking(false);
            } else if (thinkingRef.current) {
              // 思考过程中（使用 ref 判断）
              setThinkingContent((prev) => {
                const newContent = prev + content;
                thinkingContentRef.current = newContent;
                return newContent;
              });
            } else {
              // 正常回答内容
              setAnswerContent((prev) => {
                const newContent = prev + content;
                answerContentRef.current = newContent;
                return newContent;
              });
            }

            // 每收到数据就保存会话状态（用于断点续传）
            saveSession();
          }
          return;
        }

        // 4️⃣ SSE_REPLY_END 事件：结束
        if (type === 'SSE_REPLY_END') {
          const status = data.status;
          console.log('🏁 QA 收到 SSE_REPLY_END，status:', status);

          if (status === 'completed') {
            // 正常完成（相当于之前的 finish=true）
            // console.log('✅ QA 正常完成');
            setCompletedQa(true);
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            setIsCompleted(true);
            setQaParams(undefined);
            clearSession();
            setAbortController(new AbortController());
          } else if (status === 'fail' || status === 'not_exist') {
            // 异常完成：失败或事件不存在/已过期
            const errorMsg = data.message || 'QA问答异常结束';
            // console.error('❌ QA 异常结束:', status, errorMsg);
            message.error(errorMsg);
            setCompletedQa(true);
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            setIsCompleted(true);
            setQaParams(undefined);
            clearSession();
            setAbortController(new AbortController());
          } else {
            // 未知状态
            console.warn('⚠️ QA 未知的结束状态:', status);
            message.warning('QA问答结束，状态未知');
            setCompletedQa(true);
            setConnectLoading(false);
            setIsAnswering(false);
            setIsReconnecting(false);
            setIsCompleted(true);
            setQaParams(undefined);
            clearSession();
            setAbortController(new AbortController());
          }
          return;
        }

        // 未知事件类型
        console.warn('⚠️ QA 未知的事件类型:', type);
      },
      onclose() {
        console.log('🔌 QA SSE 连接关闭');
        // 注意：onclose 可能在正常结束或异常断开时都会触发
        // 如果已经通过 SSE_REPLY_END 处理了完成状态，这里不需要重复处理
        if (!isCompleted) {
          console.log('⚠️ QA 连接关闭但未完成，可能需要重连');
          setAbortController(new AbortController());
          // 🔥 不要设置 isAnswering = false，保持状态以便重连
          setIsReconnecting(false);
        }
      },
      onerror(err) {
        console.error('❌ QA SSE 连接错误:', err);
        console.log('📊 QA 错误时的状态:', {
          sessionId: sessionIdRef.current,
          cursor: cursorRef.current,
          insightId: insightIdRef.current,
          question: questionRef.current,
          isCompleted,
          isReconnecting,
        });

        // 🔥 关键改进：断开时不要设置 isAnswering = false
        // 因为会话未完成，系统会自动重连，应该保持 isAnswering = true 防止用户重复提交
        // setIsAnswering(false); // ❌ 不要设为 false
        setIsReconnecting(false);
        setConnectLoading(false);

        // 🔥 关键：不要设置 isCompleted = true，因为这只是网络错误，不是真正完成
        // 立即保存当前状态，以便重连
        if (insightIdRef.current && questionRef.current) {
          console.log('💾 保存 QA 会话状态以便重连');
          saveSession();
        }

        // 🔥 关键：抛出错误以阻止 fetchEventSource 的自动重试
        // 我们通过网络监听和心跳检测来手动控制重连
        throw err;
      },
    });
  };

  useEffect(() => {
    if (qaParams) {
      // 判断是否为重连模式
      const isReconnect = qaParams.isReconnect || false;
      contact(isReconnect);
    }
  }, [qaParams]);

  // 停止连接
  const stopContact = () => {
    abortController.abort();
    setConnectLoading(false);
    setIsAnswering(false);
    setIsReconnecting(false);
    // 保存当前状态
    saveSession();
  };

  // 重连方法
  const reconnect = () => {
    console.log('🔄 尝试重连QA，当前状态:', {
      sessionId: sessionIdRef.current,
      cursor: cursorRef.current,
      insightId: insightIdRef.current,
      question: questionRef.current,
      isCompleted,
      isReconnecting,
      isAnswering,
      reconnectAttempts: reconnectAttemptsRef.current,
    });

    // 防止重复重连
    if (isReconnecting) {
      console.log('⚠️ QA已在重连中，跳过');
      return;
    }

    // 检查重连次数是否已达上限
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('❌ QA重连次数已达上限，停止自动重连');
      message.error('网络连接已断开，多次重连失败，请手动刷新页面重试');
      setIsAnswering(false);
      return;
    }

    // 必须有 sessionId 才能断点续传重连
    // 如果没有 sessionId，说明首次连接还未建立，不应该重连
    if (!sessionIdRef.current) {
      console.log('❌ QA没有 sessionId，无法断点续传');
      return;
    }

    // 必须有 insightId 和 question
    if (!insightIdRef.current || !questionRef.current) {
      console.error('❌ QA缺少必要参数，无法重连');
      return;
    }

    // 已完成的不重连
    if (isCompleted) {
      console.log('❌ QA已完成，无需重连');
      return;
    }

    console.log('✅ QA满足重连条件，开始断点续传重连...');

    // 设置参数并触发重连
    setQaParams({
      insightId: insightIdRef.current,
      question: questionRef.current,
      isReconnect: true,
    });

    // 增加重连尝试次数，计算下一次退避间隔 - 直接更新 ref
    const nextAttempts = reconnectAttemptsRef.current + 1;
    const nextInterval = Math.min(
      baseReconnectInterval * 2 ** (nextAttempts - 1),
      maxReconnectInterval,
    );

    reconnectAttemptsRef.current = nextAttempts;
    reconnectIntervalRef.current = nextInterval;
    console.log(`📊 更新重连状态：第${nextAttempts}次重连，下次间隔${nextInterval}ms`);
  };

  // 从已保存的会话恢复并重连
  const restoreAndReconnect = (targetInsightId: string, targetQuestion: string) => {
    const session = restoreSession(targetInsightId, targetQuestion);

    if (session) {
      // 恢复状态
      setSessionId(session.sessionId);
      sessionIdRef.current = session.sessionId;
      setCursor(session.cursor);
      cursorRef.current = session.cursor;
      setThinkingContent(session.thinkingContent);
      thinkingContentRef.current = session.thinkingContent;
      setAnswerContent(session.answerContent);
      answerContentRef.current = session.answerContent;
      setGeneratedTime(session.generatedTime);
      insightIdRef.current = session.insightId;
      questionRef.current = session.question;
      setCurrentQuestion(session.question);

      // 标记为重连模式
      setIsReconnecting(true);

      // 设置参数并触发重连（通过 useEffect 自动触发 contact）
      setQaParams({
        insightId: session.insightId,
        question: session.question,
        isReconnect: true, // 添加标记
      });

      return true;
    }
    return false;
  };

  // 主动尝试从 localStorage 恢复未完成的会话（仅显示，不重连）
  const tryRestoreFromCache = (targetInsightId: string) => {
    if (hasRestoredRef.current) return false;

    const key = `qa_session_${targetInsightId}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const session = JSON.parse(saved);
        console.log('发现缓存的QA会话', session);

        // 只恢复未完成的会话
        if (!session.isCompleted && session.question) {
          // 恢复显示状态（不触发重连）
          setSessionId(session.sessionId || '');
          sessionIdRef.current = session.sessionId || '';
          setCursor(session.cursor || 0);
          cursorRef.current = session.cursor || 0;
          setThinkingContent(session.thinkingContent || '');
          thinkingContentRef.current = session.thinkingContent || '';
          setAnswerContent(session.answerContent || '');
          answerContentRef.current = session.answerContent || '';
          setGeneratedTime(session.generatedTime || '');
          insightIdRef.current = session.insightId;
          questionRef.current = session.question;
          setCurrentQuestion(session.question);
          setIsAnswering(true); // 标记为正在回答状态

          hasRestoredRef.current = true;

          // 自动尝试重连
          setTimeout(() => {
            if (!session.isCompleted && session.sessionId) {
              // 只有有 sessionId 才能断点续传
              if (session.cursor > 0) {
                message.info('检测到未完成的问答，正在恢复...');
              } else {
                message.info('检测到未完成的问答，正在重新连接...');
              }
              restoreAndReconnect(targetInsightId, session.question);
            } else if (!session.isCompleted && !session.sessionId) {
              // 没有 sessionId，说明首次连接还未建立，清除缓存
              console.log('会话没有 sessionId，清除缓存');
              localStorage.removeItem(key);
              setIsAnswering(false);
            }
          }, 500);

          return true;
        }
        // 🔥 新增：会话已完成或无效，清除缓存并重置状态
        console.log('会话已完成或无效，清除缓存');
        localStorage.removeItem(key);
        hasRestoredRef.current = true;
        // 确保状态被重置
        setIsAnswering(false);
        setIsCompleted(false);
        return false;
      }
      // 🔥 新增：没有缓存，标记已检查过
      hasRestoredRef.current = true;
      return false;
    } catch (err) {
      console.error('从缓存恢复QA会话失败', err);
      hasRestoredRef.current = true;
      return false;
    }
  };

  // Hook 初始化时尝试恢复（如果传入了 insightId）
  useEffect(() => {
    if (insightId && !hasRestoredRef.current) {
      console.log('尝试从缓存恢复会话，insightId:', insightId);
      const restored = tryRestoreFromCache(insightId);
      if (!restored) {
        console.log('没有找到未完成的会话或会话已完成');
      }
    }
  }, [insightId]);

  // 🔥 新增：组件卸载时保存当前状态并关闭连接
  useEffect(() => {
    return () => {
      // 清理定时器
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // 如果正在回答且未完成，保存状态
      if (isAnsweringRef.current && !isCompleted && insightIdRef.current && questionRef.current) {
        saveSession();
        // 主动断开 SSE 连接
        abortController.abort();
        console.log('QA组件卸载，已关闭 SSE 连接');
      }
    };
  }, [isCompleted, abortController]);

  // 🔥 新增：页面刷新/关闭前保存状态并关闭连接
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAnsweringRef.current && !isCompleted && insightIdRef.current && questionRef.current) {
        saveSession();
        // 页面关闭前也尝试断开连接（虽然浏览器会自动清理，但显式调用更规范）
        abortController.abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isCompleted, abortController]);

  return {
    connectLoading,
    thinking,
    generatedTime,
    thinkingContent,
    answerContent,
    completedQa,
    isAnswering,
    qaParams,
    sessionId,
    cursor,
    isReconnecting,
    isCompleted,
    currentQuestion,
    setCompletedQa,
    setQaParams,
    setConnectLoading,
    setIsAnswering,
    stopContact,
    setThinkingContent,
    setAnswerContent,
    reconnect,
    restoreAndReconnect,
    clearSession,
    tryRestoreFromCache,
  };
};
